import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Calendar as CalendarIcon, Plus, Edit, Trash2, CalendarDays } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { authFetch, API } from '../utils/api';
import { format } from 'date-fns';

function HolidayCalendar({ user }) {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    description: '',
    is_recurring: false
  });

  useEffect(() => {
    fetchHolidays();
  }, []);

  const fetchHolidays = async () => {
    try {
      const response = await authFetch(`${API}/holidays`);
      if (response.ok) {
        const data = await response.json();
        setHolidays(data);
      }
    } catch (error) {
      toast.error('Failed to fetch holidays');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const url = editingHoliday
        ? `${API}/holidays/${editingHoliday.holiday_id}`
        : `${API}/holidays`;

      const method = editingHoliday ? 'PUT' : 'POST';

      const response = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingHoliday ? 'Holiday updated!' : 'Holiday created!');
        setDialogOpen(false);
        setEditingHoliday(null);
        setFormData({ name: '', date: '', description: '', is_recurring: false });
        fetchHolidays();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save holiday');
      }
    } catch (error) {
      toast.error('Failed to save holiday');
    }
  };

  const handleEdit = (holiday) => {
    setEditingHoliday(holiday);
    setFormData({
      name: holiday.name,
      date: holiday.date,
      description: holiday.description || '',
      is_recurring: holiday.is_recurring
    });
    setDialogOpen(true);
  };

  const handleDelete = async (holidayId) => {
    if (!window.confirm('Are you sure you want to delete this holiday?')) return;

    try {
      const response = await authFetch(`${API}/holidays/${holidayId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Holiday deleted!');
        fetchHolidays();
      } else {
        toast.error('Failed to delete holiday');
      }
    } catch (error) {
      toast.error('Failed to delete holiday');
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingHoliday(null);
    setFormData({ name: '', date: '', description: '', is_recurring: false });
  };

  // Group holidays by year and month
  const groupedHolidays = holidays.reduce((acc, holiday) => {
    const date = new Date(holiday.date);
    const year = date.getFullYear();
    const month = format(date, 'MMMM');

    if (!acc[year]) acc[year] = {};
    if (!acc[year][month]) acc[year][month] = [];
    acc[year][month].push(holiday);

    return acc;
  }, {});

  if (user?.role !== 'admin') {
    return (
      <Layout user={user}>
        <div className="max-w-4xl mx-auto text-center py-12">
          <p className="text-foreground-muted">You don't have permission to access this page.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold text-foreground-DEFAULT">Holiday Calendar</h1>
            <p className="text-foreground-muted mt-2">Manage company holidays and observe days</p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
            <DialogTrigger asChild>
              <button className="flex items-center gap-2 bg-primary hover:bg-primary-hover text-white font-medium py-3 px-6 rounded-lg transition-all">
                <Plus className="w-5 h-5" />
                Add Holiday
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingHoliday ? 'Edit Holiday' : 'Add New Holiday'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-foreground-DEFAULT mb-2">
                    Holiday Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border-transparent focus:bg-white focus:border-primary/20 focus:ring-2 focus:ring-primary/10 transition-all rounded-lg"
                    placeholder="e.g., Christmas Day"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground-DEFAULT mb-2">
                    Date
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border-transparent focus:bg-white focus:border-primary/20 focus:ring-2 focus:ring-primary/10 transition-all rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground-DEFAULT mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border-transparent focus:bg-white focus:border-primary/20 focus:ring-2 focus:ring-primary/10 transition-all rounded-lg"
                    rows={3}
                    placeholder="Brief description of the holiday..."
                  />
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="is_recurring"
                    checked={formData.is_recurring}
                    onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked })}
                    className="w-4 h-4 text-primary rounded"
                  />
                  <label htmlFor="is_recurring" className="text-sm text-foreground-DEFAULT">
                    Recurring annually
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={handleDialogClose}
                    className="flex-1 bg-slate-100 hover:bg-slate-200 text-foreground-DEFAULT font-medium py-3 px-6 rounded-lg transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-primary hover:bg-primary-hover text-white font-medium py-3 px-6 rounded-lg transition-all"
                  >
                    {editingHoliday ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : holidays.length === 0 ? (
          <Card className="p-12 text-center">
            <CalendarDays className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-foreground-muted mb-4">No holidays added yet</p>
            <button
              onClick={() => setDialogOpen(true)}
              className="bg-primary hover:bg-primary-hover text-white font-medium py-2 px-6 rounded-lg transition-all"
            >
              Add First Holiday
            </button>
          </Card>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedHolidays)
              .sort(([a], [b]) => Number(b) - Number(a))
              .map(([year, months]) => (
                <div key={year}>
                  <h2 className="text-2xl font-bold text-foreground-DEFAULT mb-4">{year}</h2>
                  <div className="space-y-6">
                    {Object.entries(months).map(([month, monthHolidays]) => (
                      <div key={month}>
                        <h3 className="text-lg font-semibold text-foreground-DEFAULT mb-3">{month}</h3>
                        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                          {monthHolidays
                            .sort((a, b) => new Date(a.date) - new Date(b.date))
                            .map((holiday) => (
                              <Card key={holiday.holiday_id} className="p-4 hover:shadow-md transition-shadow">
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex items-start gap-3 flex-1">
                                    <div className="bg-primary/10 rounded-lg p-2">
                                      <CalendarIcon className="w-5 h-5 text-primary" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h4 className="font-semibold text-foreground-DEFAULT truncate">
                                        {holiday.name}
                                      </h4>
                                      <p className="text-sm text-foreground-muted">
                                        {format(new Date(holiday.date), 'MMMM d, yyyy')}
                                      </p>
                                      {holiday.description && (
                                        <p className="text-sm text-foreground-muted mt-2">
                                          {holiday.description}
                                        </p>
                                      )}
                                      {holiday.is_recurring && (
                                        <span className="inline-block mt-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                          Recurring
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex gap-2 mt-4 pt-4 border-t">
                                  <button
                                    onClick={() => handleEdit(holiday)}
                                    className="flex-1 flex items-center justify-center gap-2 text-sm bg-slate-100 hover:bg-slate-200 text-foreground-DEFAULT py-2 rounded-lg transition-all"
                                  >
                                    <Edit className="w-4 h-4" />
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleDelete(holiday.holiday_id)}
                                    className="flex-1 flex items-center justify-center gap-2 text-sm bg-red-50 hover:bg-red-100 text-red-600 py-2 rounded-lg transition-all"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    Delete
                                  </button>
                                </div>
                              </Card>
                            ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

export default HolidayCalendar;
